<template>
	<div class="checkout-page-container">
		<ResidentialCheckoutNavbar />
		<div class="navbar-page desktop-center">
			<div class="checkout-content sk-scrollbar">
				<div class="residential-checkout-title">
					Review and Place order
				</div>
				<div class="sk-widget-alt">
					<div class="sk-widget-user-details">
						<div class="user-details-name">
							{{ user.username }}
							<button
								class="user-details-edit"
								@click="editUserDetailsClicked()"
							>
								EDIT
							</button>
						</div>
						<div v-if="hasApartmentNumber">
							Unit {{ user.apartmentNumber }}
						</div>
						<div>
							{{ user.phone | formatInternationalPhone }}
						</div>
						<div>
							{{ user.email }}
						</div>
					</div>
					<div class="sk-widget-user-building">
						{{ businessName }}
					</div>
				</div>
				<div class="sk-widget-title">Delivery</div>
				<ResidentialDeliverySlot
					v-for="slot in deliverySlots"
					:key="slot.id"
					:deliverySlot="slot"
					:readonly="!canEditDeliveryTime"
				/>
				<CheckoutDeliveryModeSection />
				<div class="sk-widget-title">Payment</div>
				<PaymentOptions
					ref="paymentOptions"
					v-model="paymentMethodSelected"
					:options="paymentOptions"
					@methodSelected="paymentMethodClicked"
				/>
				<div class="sk-widget-alt" v-show="showCreditCardInfo">
					<div>
						<transition-group name="collapse" mode="out-in">
							<div key="1" v-show="paymentMethodSelected == 0">
								<SkDropdownSelect
									class="card-selector"
									v-model="selectedCard"
									:options="savedPaymentOptions"
									:classes="'sk-select-alternate'"
									ref="dropdown"
								/>
							</div>
							<div
								key="2"
								v-show="
									paymentMethodSelected ==
									paymentTypes.CREDIT_CARD
								"
							>
								<form id="payment-form" ref="cardOption">
									<div id="card-container"></div>
									<button
										id="card-button"
										type="button"
										style="display: none"
									></button>
								</form>
								<div id="payment-status-container"></div>
								<div class="save-cc-container">
									<SkCheckbox v-model="saveCard" />
									<span>Save Credit Card</span>
								</div>
								<input
									type="hidden"
									id="card-nonce"
									name="nonce"
								/>
							</div>
						</transition-group>
					</div>
				</div>
				<CheckoutCartSummary
					v-if="isMobile"
					@cartSummaryClicked="openCartSummary"
				/>
				<CheckoutAdditionalInfo v-if="isMobile" />
			</div>
			<FloatingCartSummary
				ref="cartSummary"
				@checkout="checkoutClicked"
			/>
			<Footer @checkout="checkoutClicked" />
		</div>
		<ItemModal ref="itemModal" />
		<EditUserAccountModal ref="editUserAccountModal" />
		<GuestCheckoutModal ref="guestCheckoutModal" />
	</div>
</template>

<style scoped>
.navbar-page {
	display: flex;
}

.sk-widget-alt {
	overflow: visible;
}

.residential-checkout-title {
	font-size: 20px;
	font-weight: 600;
	color: var(--gR-base-90);
	margin-top: 30px;
	margin-bottom: 36px;
}

.checkout-content {
	margin: 0 23px;
	padding-left: 2px;
	padding-right: 2px;
	flex: 1;
	overflow-y: auto;
}

.navbar-page >>> .alcohol-disclaimer {
	align-items: center;
	background: var(--sk-grey-bg);
	padding: 15px;
	margin: 20px 0;
	border-radius: 5px;
	border: 1px solid var(--gR-white2);
	font-size: 10px;
}

.navbar-page >>> .alcohol-disclaimer i {
	margin-right: 20px;
}

.checkout-content >>> .checkout-additional-info {
	padding: 0;
}

.save-cc-container {
	margin-top: 20px;
	display: flex;
	align-items: center;
}

.save-cc-container span {
	font-size: 12px;
	color: var(--sk-grey2);
}
</style>

<script>
import { NotificationType } from "@/enums/NotificationType"
import { eventBus, EventBusEvents } from "@/eventBus"
import { MenuType } from "@reef-os/utils/enums/menuType"
import { CouponType } from "@reef-os/utils/enums/couponType"
import { PaymentType } from "@reef-os/utils/enums/paymentType"
import utils from "@/utils"
import moment from "moment"
import momentUtils from "@/moment-utils"
import currencyFilterMixin from "@/mixins/currency-filter-mixin"
import phoneFilterMixin from "@/mixins/phone-filter-mixin"
import pluralizeFilterMixin from "@/mixins/pluralize-filter-mixin"
import mobileResponsivenessMixin from "@/mixins/mobile-responsiveness-mixin"
import squareFormMixin from "@/mixins/square-form-mixin"
import checkoutMixin from "@/mixins/checkout-mixin"
import loaderMixin from "@/mixins/loader-mixin"
import ResidentialCheckoutNavbar from "@/components/navbars/ResidentialCheckoutNavbar.vue"
import FloatingCartSummary from "@/components/common/FloatingCartSummary.vue"
import SkDropdownSelect from "@/components/common/SkDropdownSelect.vue"
import SkCheckbox from "@/components/common/SkCheckbox.vue"
import PaymentOptions from "@/components/checkout/PaymentOptions.vue"
import ResidentialDeliverySlot from "@/components/checkout/ResidentialDeliverySlot.vue"
import CheckoutDeliveryModeSection from "@/components/checkout/CheckoutDeliveryModeSection.vue"
import CheckoutAdditionalInfo from "@/components/checkout/CheckoutAdditionalInfo.vue"
import CheckoutCartSummary from "@/components/checkout/CheckoutCartSummary.vue"
import Footer from "@/components/Footer.vue"
import ItemModal from "@/components/modals/ItemModal.vue"
import EditUserAccountModal from "@/components/modals/EditUserAccountModal.vue"
import GuestCheckoutModal from "@/components/modals/GuestCheckoutModal.vue"

export default {
	name: "ResidentialCheckout",
	mixins: [
		currencyFilterMixin,
		loaderMixin,
		phoneFilterMixin,
		mobileResponsivenessMixin,
		squareFormMixin,
		checkoutMixin,
		pluralizeFilterMixin
	],
	components: {
		ResidentialCheckoutNavbar,
		SkDropdownSelect,
		SkCheckbox,
		PaymentOptions,
		ResidentialDeliverySlot,
		CheckoutDeliveryModeSection,
		FloatingCartSummary,
		CheckoutAdditionalInfo,
		CheckoutCartSummary,
		Footer,
		ItemModal,
		EditUserAccountModal,
		GuestCheckoutModal
	},
	data: function () {
		return {
			selectedCard: {
				id: null
			},
			mobileThresholdWidth: 900,
			paymentMethodSelected: null,
			saveCard: false,
			paymentTypes: PaymentType
		}
	},
	computed: {
		businessName() {
			return this.$store.state.business && this.$store.state.business.name
				? this.$store.state.business.name
				: ""
		},
		paymentOptions() {
			const options = this.paymentMethods.slice()
			if (
				this.googlePaySupported &&
				this.allowedPaymentMethods.includes(PaymentType.GOOGLE_PAY)
			) {
				options.push({
					icon: "sk-icon-google-pay-brands",
					id: PaymentType.GOOGLE_PAY
				})
			}

			if (
				this.applePaySupported &&
				this.allowedPaymentMethods.includes(PaymentType.APPLE_PAY)
			) {
				options.push({
					icon: "sk-icon-apple-pay",
					id: PaymentType.APPLE_PAY
				})
			}
			return options
		},
		paymentMethodObserver() {
			return (
				this.allowedPaymentMethods &&
				this.paymentMethods &&
				this.paymentOptions
			)
		},
		paymentMethods() {
			const methods = []

			if (this.allowedPaymentMethods.includes(PaymentType.CREDIT_CARD)) {
				if (this.savedPaymentOptions.length > 0) {
					methods.push({
						text: "Saved Card",
						id: 0
					})
				}

				methods.push({
					text: "New Card",
					id: PaymentType.CREDIT_CARD
				})
			}

			return methods
		},
		savedPaymentOptions() {
			return this.$store.state.user.paymentMethods
				? this.$store.state.user.paymentMethods.map((method) => {
						return {
							id: method.id,
							text: `${method.card_brand} ... ${method.card_last_4_digits}`,
							isDefault: method.is_default == 1
						}
				  })
				: []
		},
		showCreditCardInfo() {
			return (
				this.paymentMethodSelected == 0 ||
				this.paymentMethodSelected == PaymentType.CREDIT_CARD
			)
		},
		user() {
			return this.$store.state.user
		},
		hasApartmentNumber() {
			return (
				this.$store.state.user &&
				this.$store.state.user.apartmentNumber.length > 0
			)
		},
		itemsInCartCount() {
			return this.$store.getters.itemsInCartCount
		},
		deliverySlots() {
			const slots = {}
			this.$store.state.cart.forEach((cartItem) => {
				const menuId = cartItem.menu_id
				const key = `${menuId}-${cartItem.delivery_date}`
				if (slots[key] == undefined) {
					const date = moment(cartItem.delivery_date, "YYYY-MM-DD")
					const menu = this.$store.getters.menusHash[menuId]
						? this.$store.getters.menusHash[menuId].menu
						: {}
					const dow = momentUtils.normalizeDayOfWeek(date)
					const menuSchedule =
						this.$store.state.menuSchedules[menu.id]
					slots[key] = {
						id: key,
						deliveryDate: cartItem.delivery_date,
						deliveryTime: cartItem.delivery_time,
						itemQuantity: 0,
						menuName: menu ? menu.name : "",
						menuId: menuId,
						menuInStock: menu.stock_status == 1,
						dailySchedule:
							menuSchedule && menuSchedule[dow]
								? menuSchedule[dow]
								: []
					}
				}
				slots[key].itemQuantity += cartItem.quantity
			})
			return Object.values(slots)
		},
		canEditDeliveryTime() {
			return (
				this.$store.getters.cartMenuType == MenuType.DEFAULT ||
				this.$store.getters.cartMenuType == MenuType.EMBEDDED ||
				this.$store.getters.cartMenuType == MenuType.ESSENTIALS
			)
		}
	},
	methods: {
		methodsSupported(supportedMethods) {
			if (supportedMethods.applePay) {
				this.applePaySupported = supportedMethods.applePay === true
			}
			if (supportedMethods.googlePay) {
				this.googlePaySupported = supportedMethods.googlePay === true
			}
		},
		paymentMethodClicked(id) {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Payment Method Set",
				data: {
					id: id
				}
			})
			this.paymentMethodSelected = id
		},
		checkoutClicked() {
			if (this.$store.getters.totalCheckoutPrice <= 0) {
				if (!this.$store.state.inPurchaseState) {
					this.$store.dispatch("sendMixpanelEvent", {
						msg: "Click Purchase",
						data: {
							type: "Voucher"
						}
					})
					this.toggleLoader(true)
					this.$store.dispatch("processVoucherOrder")
				}
				return
			}
			switch (this.paymentMethodSelected) {
				case 0: {
					if (this.selectedCard && this.selectedCard.id) {
						this.toggleLoader(true)
						this.chargeCard("", {
							cardData: {
								digital_wallet_type: "CREDIT_CARD"
							},
							cardId: this.selectedCard.id
						})
					} else {
						eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
							message: "Please select a card",
							type: NotificationType.ERROR
						})
					}
					break
				}
				case PaymentType.CREDIT_CARD: {
					this.clickPurchase()
					break
				}
				case PaymentType.GOOGLE_PAY: {
					this.$refs.paymentOptions.$refs.googlePayBtn.click()
					break
				}
				case PaymentType.APPLE_PAY: {
					this.$refs.paymentOptions.$refs.applePayBtn.click()
					break
				}
				default: {
					eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
						message: "Please select a payment option",
						type: NotificationType.WARNING
					})
					throw "Missing payment option"
				}
			}
		},
		onCardNonceError(errors) {
			let errorMsg = errors[0].message
			eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
				message: errorMsg,
				type: NotificationType.ERROR
			})
		},
		inputEventReceived(inputEvent) {
			switch (inputEvent.eventType) {
				case "errorClassAdded":
					this.toggleLoader(false)
					break
			}
		},
		editUserDetailsClicked() {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Edit User Details Clicked"
			})
			this.$refs.editUserAccountModal.open()
		},
		requiresLogin() {
			return !this.$store.state.user || this.$store.state.user.id == 0
		},
		selectDefaultCard() {
			if (this.savedPaymentOptions.length > 0) {
				this.paymentMethodSelected = 0
				const defaultCard = this.savedPaymentOptions.find(
					(option) => option.isDefault
				)
				if (defaultCard != undefined) {
					this.selectedCard = defaultCard
				}
			} else {
				this.paymentMethodSelected = PaymentType.CREDIT_CARD
			}
		},
		async getPaymentMethods() {
			await this.$store.dispatch("getUserPaymentMethods")
			if (this.allowedPaymentMethods.includes(PaymentType.CREDIT_CARD)) {
				this.selectDefaultCard()
			}
		},
		async autoApplyCouponIfNeeded() {
			const coupon = this.$store.state.coupon
			const hasCoupon = coupon && coupon.id != 0
			const smsOptedIn = this.$store.state.user.smsOptIn
			if (!hasCoupon) {
				await this.$store.dispatch("getUserCoupons")
				let couponToApply = null
				let couponValue = 0
				this.$store.state.user.coupons.forEach((coupon) => {
					if (
						coupon.type != 1 &&
						(coupon.coupon_type == CouponType.LOYALTY ||
							coupon.coupon_type == CouponType.FIRST_TIME) &&
						coupon.amount > couponValue
					) {
						couponToApply = coupon
						couponValue = coupon.amount
					}
				})
				if (couponToApply) {
					this.$store.dispatch("calcDiscount", couponToApply.code)
				} else if (smsOptedIn) {
					this.$store.dispatch("calcDiscount", "SMSOPTIN")
				}
			} else {
				const regex = /SMSOPTIN/i
				let invalidCoupon = false
				if (coupon.couponType == CouponType.FIRST_TIME) {
					let firstTimeCoupon = this.$store.state.user.coupons.find(
						(coupon) => coupon.coupon_type == CouponType.FIRST_TIME
					)
					if (!firstTimeCoupon) {
						await this.$store.dispatch("getUserCoupons")
					}
					firstTimeCoupon = this.$store.state.user.coupons.find(
						(coupon) => coupon.coupon_type == CouponType.FIRST_TIME
					)
					if (!firstTimeCoupon || !firstTimeCoupon.valid_to) {
						invalidCoupon = true
					} else if (
						firstTimeCoupon.valid_from &&
						firstTimeCoupon.valid_to
					) {
						const now = moment.utc()
						const validFrom = moment
							.utc(firstTimeCoupon.valid_from)
							.format("YYYY-MM-DD")
						const validTo = moment
							.utc(firstTimeCoupon.valid_to)
							.format("YYYY-MM-DD")
						if (
							!momentUtils.isBetweenDays(now, validFrom, validTo)
						) {
							invalidCoupon = true
						}
					}
				} else if (regex.test(coupon.code) && !smsOptedIn) {
					invalidCoupon = true
				}

				if (invalidCoupon) {
					this.$store.dispatch("removeCoupon")
				}
			}
		},
		beforeRouteUpdate: function (to) {
			this.detectItemModalRoute(to)
		},
		openCartSummary() {
			this.$refs.cartSummary.open()
		}
	},
	mounted: async function () {
		if (this.$store.getters.isGuestCheckoutAvailable) {
			const promise = utils.createPromise()
			this.$refs.guestCheckoutModal.open({
				resolve: promise.resolve
			})
			await promise.promise
			if (this.$store.state.temporaryFlag.isGuestCheckoutActive) {
				return false
			}
		}
		if (!this.$store.state.user || this.$store.state.user.id == 0) {
			const promise = utils.createPromise()
			eventBus.emit(EventBusEvents.OPEN_VERIFY_FLOW, {
				required: true,
				resolve: promise.resolve
			})
			await promise.promise
		}
		this.getPaymentMethods()
		this.autoApplyCouponIfNeeded()
	},
	watch: {
		saveCard(newVal) {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Save Card Clicked",
				data: {
					save: newVal ? 1 : 0
				}
			})
		},
		paymentMethodObserver: {
			immediate: true,
			handler() {
				const methods = this.allowedPaymentMethods
				if (this.paymentMethodSelected == null && methods.length > 0) {
					if (methods.includes(PaymentType.CREDIT_CARD)) {
						this.selectDefaultCard()
					} else {
						const option = this.paymentOptions.find((option) =>
							methods.includes(option.id)
						)
						if (option) {
							this.paymentMethodSelected = option.id
						}
					}
				}
			}
		}
	}
}
</script>